import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../api";
import { Icon, Spinner, Empty, Modal, useToast } from "../../components/ui";
import { derive, applyRefData } from "../admin/recordDerive";
import { RecordModal } from "../admin/Inventory";

export function ArchiveCenter() {
  const toast = useToast();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState(null);
  const [archived, setArchived] = useState(null);
  const [viewing, setViewing] = useState(null);      // archived record → read-only view
  const [reviewing, setReviewing] = useState(null);   // archive request → approve/reject dialog
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(null);             // archived tab: restore

  const load = useCallback(async () => {
    try {
      const [reqs, arch, ref] = await Promise.all([
        api.listArchiveRequests(), api.listArchived(), api.getRefData().catch(() => null),
      ]);
      applyRefData(ref); setRequests(reqs); setArchived(arch);
    } catch (e) { toast(e.message, "err"); setRequests([]); setArchived([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const rows = tab === "requests" ? requests : archived;

  const act = async (fn, id, msg) => {
    setBusy(true);
    try { await fn([id]); toast(msg, "ok"); setReviewing(null); setMenu(null); load(); }
    catch (e) { toast(e.message, "err"); }
    finally { setBusy(false); }
  };

  if (requests === null) return <div className="empty"><Spinner /></div>;

  return (
    <div>
      <div className="inv-tabs">
        <button className={"inv-tab" + (tab === "requests" ? " on" : "")} onClick={() => setTab("requests")}>
          Archive requests<span className="proc-n">{requests.length}</span>
        </button>
        <button className={"inv-tab" + (tab === "archived" ? " on" : "")} onClick={() => setTab("archived")}>
          Archived<span className="proc-n">{archived === null ? "…" : archived.length}</span>
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        {tab === "requests" ? "Click a request to review it — approve or reject the archive in the dialog." : "Right-click to restore a record to active. Click to view."}
      </p>

      {(rows || []).length === 0
        ? <Empty icon={<Icon.clipboard width="40" height="40" />} title={tab === "requests" ? "No archive requests" : "Nothing archived"}
            text={tab === "requests" ? "When someone requests an archive, it shows up here for your approval." : "Approved archives will be listed here."} />
        : <div className="card"><div className="table-wrap">
            <table className="tbl arch-tbl">
              <thead><tr>
                <th>PID</th><th>Order</th><th>RTC Date</th><th>Group</th><th>Payor / IPA</th>
                <th>{tab === "requests" ? "Requested by" : "Status"}</th>
                <th>UID</th>
                {tab === "requests" && <th>Reason</th>}
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const d = derive(r);
                  return (
                    <tr key={r.id} className="row-click"
                      onClick={() => (tab === "requests" ? setReviewing(r) : setViewing(r))}
                      onContextMenu={(e) => { if (tab !== "archived") return; e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, record: r }); }}>
                      <td className="mono">{r.mrn}</td>
                      <td className="cell-sub">{d.orders}</td>
                      <td className="cell-sub">{d.rtcDate || "—"}</td>
                      <td>{r.productionGroup || d.location}</td>
                      <td className="cell-sub">{d.insurance || "—"}</td>
                      <td className="cell-sub">{tab === "requests" ? (r.archiveRequestedBy || "—") : "Archived"}</td>
                      <td className="mono uid-wrap">{r.uniqueId}</td>
                      {tab === "requests" && <td className="reason-wrap">{r.archiveReason || "—"}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div></div>}

      {menu && (
        <>
          <div className="ctx-backdrop" onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
          <div className="ctx-menu" style={{ left: menu.x, top: menu.y }}>
            <button className="ctx-item" onClick={() => act(api.rejectArchive, menu.record.id, "Restored to active")}>Restore to active</button>
          </div>
        </>
      )}

      {reviewing && (
        <ArchiveReviewModal record={reviewing} busy={busy}
          onClose={() => setReviewing(null)}
          onApprove={() => act(api.approveArchive, reviewing.id, "Record archived")}
          onReject={() => act(api.rejectArchive, reviewing.id, "Request rejected")} />
      )}

      {viewing && <RecordModal record={viewing} onClose={() => setViewing(null)} isSA={true} />}
    </div>
  );
}

function ArchiveReviewModal({ record, busy, onClose, onApprove, onReject }) {
  const d = derive(record);
  const reqOn = (() => { try { return record.archiveRequestedAt ? new Date(record.archiveRequestedAt).toLocaleString() : "—"; } catch { return "—"; } })();
  const rows = [
    ["Clinic Location", d.clinic], ["Location Code", d.locationCode], ["Group", record.productionGroup || d.location],
    ["Order", d.orders], ["OrderMD", d.orderMD], ["Order Date", d.orderDate],
    ["RTC Date", d.rtcDate], ["RTC Priority", d.rtcPriority], ["Urgent / Routine", d.urgentRoutine],
    ["Payor / IPA", d.insurance], ["Portal", d.portal],
    ["CPTs", record.submittedCpts], ["ICDs", record.submittedIcds],
    ["Status", record.paStatus], ["Inbox", record.inboxValue],
    ["Requested by", record.archiveRequestedBy], ["Requested on", reqOn],
    ["Unique ID", record.uniqueId],
  ];
  return (
    <Modal wide title={`Archive request — PID ${record.mrn}`} sub="Review the reason and details, then approve or reject."
      onClose={onClose}
      foot={<>
        <button className="btn btn-danger" onClick={onReject} disabled={busy}>Reject request</button>
        <button className="btn btn-primary" onClick={onApprove} disabled={busy}>{busy ? <span className="spin" /> : "Approve archive"}</button>
      </>}>
      <div className="arch-reason-box">
        <div className="arch-reason-l">Reason for archive</div>
        <div className="arch-reason-v">{record.archiveReason || "— no reason given —"}</div>
      </div>
      <div className="fit-grid" style={{ marginTop: 14 }}>
        {rows.filter(([, v]) => v && v !== "—").map(([k, v]) => <div className="fit-cell" key={k}><span>{k}</span><b className={k === "Unique ID" ? "uid-wrap" : ""}>{v}</b></div>)}
      </div>
    </Modal>
  );
}